// ─────────────────────────────────────────────────────────────
// stockReport.config.js — Report 3: complete stock report
// Shape: statement (portrait) — three sections
//   1. Jumbo rolls in stock       (remaining meters × cost/m)
//   2. Cores in hand              (grouped by tape type)
//   3. Finished stock             (open vs customer-locked)
//
// Values use cost_per_meter / cost_per_core / moving-average,
// never `rate` — rate excludes freight, GST and other charges.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// ── Section 1 — jumbo rolls ──────────────────────────────────
const jumboSection = (rolls) => {
  const rows = rolls.map((r) => ({
    roll: r.vendor_roll_number,
    jumbo: r.jumbo_name,
    vendor: r.vendor_name,
    total_meters: r.total_meters,
    consumed_meters: r.consumed_meters,
    remaining_meters: r.remaining_meters,
    cost_per_meter: r.cost_per_meter,
    value: num(r.remaining_meters) * num(r.cost_per_meter),
  }));

  const totalRemaining = rows.reduce((s, r) => s + num(r.remaining_meters), 0);
  const totalValue = rows.reduce((s, r) => s + r.value, 0);

  return {
    title: 'Jumbo Rolls in Stock',
    tone: 'net',
    columns: [
      { key: 'roll', label: 'Roll #', align: 'left', fmt: 'text', width: 28 },
      { key: 'jumbo', label: 'Jumbo', align: 'left', fmt: 'text' },
      { key: 'vendor', label: 'Vendor', align: 'left', fmt: 'text', width: 28 },
      { key: 'total_meters', label: 'Received', align: 'right', fmt: 'meters', width: 22 },
      { key: 'consumed_meters', label: 'Used', align: 'right', fmt: 'meters', width: 22 },
      { key: 'remaining_meters', label: 'Remaining', align: 'right', fmt: 'meters', width: 24 },
      { key: 'cost_per_meter', label: 'Cost / m', align: 'right', fmt: 'rate', width: 20 },
      { key: 'value', label: 'Value', align: 'right', fmt: 'money', width: 26, bold: true },
    ],
    rows,
    footRow: [
      `${rows.length} ROLLS`,
      '', '', '', '',
      FORMATTERS.meters(totalRemaining),
      '',
      FORMATTERS.money(totalValue),
    ],
    __value: totalValue,
  };
};

// ── Section 2 — cores in hand, grouped by tape type ──────────
const coreSection = (batches) => {
  // Group by tape type so the owner reads it spec-wise:
  // Clear Tape cores and Paper Tape cores are not interchangeable.
  const groups = {};
  batches.forEach((b) => {
    const key = b.tape_type_name || 'Unspecified';
    if (!groups[key]) groups[key] = [];
    groups[key].push(b);
  });

  const rows = [];
  let grandCores = 0;
  let grandValue = 0;

  Object.keys(groups).sort().forEach((type) => {
    let typeCores = 0;
    let typeValue = 0;

    groups[type].forEach((b) => {
      const left = num(b.cores_remaining);
      const value = left * num(b.cost_per_core);
      typeCores += left;
      typeValue += value;

      rows.push({
        batch: b.batch_number,
        date: b.production_date,
        type,
        spec: `${num(b.core_width_mm)} mm × ${FORMATTERS.int(b.core_length_meters)} m`,
        jumbo: b.jumbo_name,
        cores_remaining: left,
        cost_per_core: b.cost_per_core,
        value,
      });
    });

    // Per-type subtotal, rendered bold and shaded by the engine
    rows.push({
      __subtotal: true,
      batch: `${type} — Subtotal`,
      cores_remaining: typeCores,
      value: typeValue,
    });

    grandCores += typeCores;
    grandValue += typeValue;
  });

  return {
    title: 'Cores in Hand',
    tone: 'net',
    columns: [
      { key: 'batch', label: 'Batch #', align: 'left', fmt: 'text', width: 24 },
      { key: 'date', label: 'Date', align: 'left', fmt: 'date', width: 24 },
      { key: 'type', label: 'Tape Type', align: 'left', fmt: 'text', width: 26 },
      { key: 'spec', label: 'Core Spec', align: 'left', fmt: 'text' },
      { key: 'jumbo', label: 'From Jumbo', align: 'left', fmt: 'text' },
      { key: 'cores_remaining', label: 'Cores', align: 'right', fmt: 'int', width: 18 },
      { key: 'cost_per_core', label: 'Cost / Core', align: 'right', fmt: 'money2', width: 24 },
      { key: 'value', label: 'Value', align: 'right', fmt: 'money', width: 26, bold: true },
    ],
    rows,
    footRow: [
      'TOTAL CORES',
      '', '', '', '',
      FORMATTERS.int(grandCores),
      '',
      FORMATTERS.money(grandValue),
    ],
    __value: grandValue,
  };
};

// ── Section 3 — finished stock ───────────────────────────────
const finishedSection = (stock) => {
  // Open stock and customer-locked stock are kept apart —
  // merged, they would suggest more is sellable than really is.
  const open = stock.filter((s) => !s.customer_id);
  const locked = stock.filter((s) => s.customer_id);

  const rows = [];
  let grandQty = 0;
  let grandValue = 0;

  const pushGroup = (list, label) => {
    if (list.length === 0) return;
    let qty = 0;
    let value = 0;

    list.forEach((s) => {
      const q = num(s.quantity);
      const v = num(s.total_cost);
      qty += q;
      value += v;

      rows.push({
        product: s.product_name,
        size: `${s.size_inches}"`,
        type: s.tape_type_name,
        holder: s.customer_name
          ? `${s.customer_name}${s.print_design ? ` · ${s.print_design}` : ''}`
          : 'Open Stock',
        quantity: q,
        // Moving average — total_cost is a pool, not per unit
        avg_cost: q > 0 ? v / q : 0,
        value: v,
      });
    });

    rows.push({
      __subtotal: true,
      product: `${label} — Subtotal`,
      quantity: qty,
      value,
    });

    grandQty += qty;
    grandValue += value;
  };

  pushGroup(open, 'Open Stock');
  pushGroup(locked, 'Customer Locked');

  return {
    title: 'Finished Stock',
    tone: 'net',
    columns: [
      { key: 'product', label: 'Product', align: 'left', fmt: 'text' },
      { key: 'size', label: 'Size', align: 'center', fmt: 'text', width: 18 },
      { key: 'type', label: 'Tape Type', align: 'left', fmt: 'text', width: 26 },
      { key: 'holder', label: 'Held For', align: 'left', fmt: 'text' },
      { key: 'quantity', label: 'Rolls', align: 'right', fmt: 'int', width: 18 },
      { key: 'avg_cost', label: 'Avg Cost', align: 'right', fmt: 'money2', width: 24 },
      { key: 'value', label: 'Value', align: 'right', fmt: 'money', width: 26, bold: true },
    ],
    rows,
    footRow: [
      'TOTAL FINISHED',
      '', '', '',
      FORMATTERS.int(grandQty),
      '',
      FORMATTERS.money(grandValue),
    ],
    __value: grandValue,
  };
};

// ── Main ─────────────────────────────────────────────────────
export const stockReportConfig = ({ rolls, batches, stock }) => {
  // Endpoints don't all wrap their payload the same way, so
  // anything that isn't an array is treated as an empty section
  // rather than being allowed to crash the whole report.
  const arr = (v) => (Array.isArray(v) ? v : []);

  const s1 = jumboSection(arr(rolls));
  const s2 = coreSection(arr(batches));
  const s3 = finishedSection(arr(stock));

  const total = s1.__value + s2.__value + s3.__value;

  return {
    kind: 'statement',
    orientation: 'l',
    title: 'Stock Report',
    subtitle: `As at ${FORMATTERS.date(new Date())}`,

    boxes: [
      {
        heading: 'Stock Position',
        rows: [
          { label: 'Jumbo Rolls', value: `${arr(rolls).length} rolls` },
          { label: 'Core Batches', value: `${arr(batches).length} batches` },
          { label: 'Finished Lines', value: `${arr(stock).length} lines` },
        ],
      },
      {
        heading: 'Value Summary',
        rows: [
          { label: 'Jumbo Rolls', value: FORMATTERS.money(s1.__value) },
          { label: 'Cores', value: FORMATTERS.money(s2.__value) },
          { label: 'Finished Stock', value: FORMATTERS.money(s3.__value) },
          { label: 'Total Inventory', value: FORMATTERS.money(total) },
        ],
      },
    ],

    sections: [s1, s2, s3],

    totals: [
      { label: 'Jumbo Roll Value', value: s1.__value, fmt: 'money' },
      { label: 'Core Value', value: s2.__value, fmt: 'money' },
      { label: 'Finished Stock Value', value: s3.__value, fmt: 'money' },
      { label: 'Total Inventory', value: total, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    signatures: SIGNATURES.report,
  };
};

export default stockReportConfig;